import { useEffect, useRef, useState } from "react";
import { GUEST_LOCALE_LABELS, SUPPORTED_GUEST_LOCALES, t } from "../../i18n";
import type { Locale } from "../../i18n/types";

type GuestLanguageSwitcherProps = {
  locale: Locale;
  onChange: (locale: Locale) => void;
  align?: "left" | "right";
  className?: string;
  compact?: boolean;
};

const join = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

function GuestLanguageSwitcher({
  locale,
  onChange,
  align = "right",
  className,
  compact = false,
}: GuestLanguageSwitcherProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <div className={join("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("guest.language.label")}
        onClick={() => setOpen((prev) => !prev)}
        className={join(
          "flex items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-slate-600 dark:hover:bg-slate-800",
          compact ? "h-10 w-10 p-0 text-xs" : "h-9 gap-2 px-3",
        )}
      >
        <span>{locale.toUpperCase()}</span>
      </button>
      {open ? (
        <div
          ref={popoverRef}
          role="menu"
          className={join(
            "absolute z-40 mt-2 w-44 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900",
            align === "left" ? "left-0" : "right-0",
          )}
        >
          <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t("guest.language.label")}
          </div>
          <div className="space-y-1">
            {SUPPORTED_GUEST_LOCALES.map((option) => {
              const active = option === locale;
              return (
                <button
                  key={option}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                  className={join(
                    "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition",
                    active
                      ? "bg-indigo-50 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-100"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
                  )}
                >
                  <span className="font-semibold">{option.toUpperCase()}</span>
                  <span className="text-xs">{GUEST_LOCALE_LABELS[option]}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default GuestLanguageSwitcher;
